import React, { useMemo, useState } from "react";
import TeamLogo from "@/components/TeamLogo";
import { Progress } from "@/components/ui/progress";
import { ChevronRight, Route, Scale, Trophy } from "lucide-react";

/**
 * Body of the bottom Sheet that opens when a probability card is tapped.
 * Two sections:
 *   1) Per-fixture What-Ifs — for every remaining match, "If A wins → team's chance becomes X%".
 *   2) Sample Qualifying Paths — up to 50 representative scenarios where this team finishes top-4.
 */
const ProbabilityDetail = ({
  short,
  team,
  probability,
  samplePaths,
  whatIf,
  fixturesById,
  teamMap,
  standingsByShort,
  method,
  totalScenarios,
}) => {
  const [tab, setTab] = useState("whatif");

  // Build sorted what-if list: fixtures involving this team first, then by largest delta.
  const whatIfRows = useMemo(() => {
    const rows = [];
    for (const fid of Object.keys(whatIf)) {
      const f = fixturesById[fid];
      if (!f) continue;
      const homeProb = whatIf[fid]?.home?.[short];
      const awayProb = whatIf[fid]?.away?.[short];
      if (homeProb == null || awayProb == null) continue;
      const delta = Math.abs(homeProb - awayProb);
      const involves = f.home === short || f.away === short;
      rows.push({ f, homeProb, awayProb, delta, involves });
    }
    rows.sort((a, b) => {
      if (a.involves !== b.involves) return a.involves ? -1 : 1;
      return b.delta - a.delta;
    });
    return rows;
  }, [whatIf, fixturesById, short]);

  const row = standingsByShort[short];

  return (
    <div className="px-4 py-5 space-y-5" data-testid="probability-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <TeamLogo team={team} size={44} />
        <div className="flex-1">
          <div className="font-display uppercase text-lg leading-none">{team?.name}</div>
          <div className="text-[11px] text-muted-foreground mt-1 uppercase tracking-widest">
            {row && (
              <>
                Pos {row.pos} · {row.Pts} pts · NRR {row.NRR.toFixed(3)}
              </>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="num text-3xl leading-none">{probability.toFixed(1)}%</div>
          <div className="text-[9px] text-muted-foreground tracking-widest uppercase mt-1">
            top-4 chance
          </div>
        </div>
      </div>

      {/* Inline tabs */}
      <div className="flex gap-2 text-[11px] uppercase tracking-widest font-display border-b border-border">
        <TabBtn active={tab === "whatif"} onClick={() => setTab("whatif")} testId="detail-tab-whatif">
          <Scale className="w-3.5 h-3.5 mr-1.5" />
          What-Ifs
        </TabBtn>
        <TabBtn active={tab === "paths"} onClick={() => setTab("paths")} testId="detail-tab-paths">
          <Route className="w-3.5 h-3.5 mr-1.5" />
          Sample Paths
        </TabBtn>
      </div>

      {tab === "whatif" && (
        <WhatIfList rows={whatIfRows} teamMap={teamMap} probability={probability} />
      )}

      {tab === "paths" && (
        <PathsList paths={samplePaths} fixturesById={fixturesById} teamMap={teamMap} short={short} />
      )}

      <div className="text-[10px] text-muted-foreground leading-relaxed pt-2 border-t border-border">
        {method === "brute_force"
          ? `Computed by exact enumeration of ${totalScenarios.toLocaleString()} scenarios.`
          : method === "final"
          ? `League stage complete — these are the final positions.`
          : `Estimated from ${totalScenarios.toLocaleString()} Monte-Carlo simulations (50/50 W/L per match, fixed +0.1 NRR margin per win).`}
      </div>
    </div>
  );
};

const TabBtn = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`pb-2 px-1 -mb-px border-b-2 inline-flex items-center ${
      active
        ? "border-primary text-primary"
        : "border-transparent text-muted-foreground hover:text-foreground"
    }`}
  >
    {children}
  </button>
);

const WhatIfList = ({ rows, teamMap, probability }) => {
  if (rows.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4">
        No remaining fixtures — chances are locked at {probability.toFixed(1)}%.
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="whatif-list">
      <div className="text-[11px] text-muted-foreground">
        How each remaining fixture moves this team's qualification chance.
      </div>
      {rows.map(({ f, homeProb, awayProb, involves }) => (
        <WhatIfRow
          key={f.id}
          fixture={f}
          homeProb={homeProb}
          awayProb={awayProb}
          base={probability}
          teamMap={teamMap}
          involves={involves}
        />
      ))}
    </div>
  );
};

const WhatIfRow = ({ fixture, homeProb, awayProb, base, teamMap, involves }) => {
  const homeDelta = homeProb - base;
  const awayDelta = awayProb - base;
  return (
    <div
      className={`bg-card border rounded-sm px-3 py-2.5 ${
        involves ? "border-primary/30" : "border-border"
      }`}
      data-testid={`whatif-row-${fixture.id}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
        <span>{fixture.id}</span>
        <span>·</span>
        <span>{fixture.date}</span>
        {involves && <span className="text-primary ml-auto">Featuring this team</span>}
      </div>
      <div className="grid grid-cols-2 gap-2 mt-1.5">
        <OutcomeBox
          label="If"
          team={teamMap[fixture.home]}
          subtitle="wins"
          opponent={teamMap[fixture.away]}
          prob={homeProb}
          delta={homeDelta}
        />
        <OutcomeBox
          label="If"
          team={teamMap[fixture.away]}
          subtitle="wins"
          opponent={teamMap[fixture.home]}
          prob={awayProb}
          delta={awayDelta}
        />
      </div>
    </div>
  );
};

const OutcomeBox = ({ label, team, subtitle, opponent, prob, delta }) => {
  const upish = delta > 0.1;
  const downish = delta < -0.1;
  const tone = upish ? "text-primary" : downish ? "text-destructive" : "text-muted-foreground";
  return (
    <div className="bg-secondary/40 border border-border rounded-sm px-2 py-1.5">
      <div className="flex items-center gap-1.5 text-[11px]">
        <TeamLogo team={team} size={18} />
        <span className="font-display uppercase">{team?.short}</span>
        <span className="text-muted-foreground text-[10px]">{subtitle}</span>
      </div>
      <div className="flex items-end justify-between mt-1">
        <div className="num text-xl leading-none">{prob.toFixed(1)}%</div>
        <div className={`text-[10px] tracking-widest uppercase ${tone}`}>
          {delta > 0 ? "+" : ""}
          {delta.toFixed(1)}
        </div>
      </div>
      <Progress value={prob} className="h-1 mt-1 bg-background" />
      <div className="text-[9px] text-muted-foreground mt-1 truncate">
        vs {opponent?.short}
      </div>
    </div>
  );
};

const PathsList = ({ paths, fixturesById, teamMap, short }) => {
  if (paths.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-4">
        No qualifying scenarios were observed for this team in our sample.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="paths-list">
      <div className="text-[11px] text-muted-foreground">
        Showing <span className="text-foreground num">{paths.length}</span> representative scenarios where{" "}
        <span className="font-display uppercase">{short}</span> finishes in the top 4.
      </div>
      {paths.map((path, i) => (
        <PathItem
          key={i}
          index={i + 1}
          path={path}
          fixturesById={fixturesById}
          teamMap={teamMap}
          highlightTeam={short}
        />
      ))}
    </div>
  );
};

const PathItem = ({ index, path, fixturesById, teamMap, highlightTeam }) => {
  const [open, setOpen] = useState(false);
  const ownMatches = path.filter((step) => {
    const f = fixturesById[step.fid];
    return f && (f.home === highlightTeam || f.away === highlightTeam);
  });
  const ownWins = ownMatches.filter((s) => s.winner === highlightTeam).length;
  const ownLosses = ownMatches.length - ownWins;
  return (
    <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid={`path-${index}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-secondary/40 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Trophy className="w-3.5 h-3.5 text-primary" />
          <span className="font-display uppercase text-sm">Path #{index}</span>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {ownWins}W · {ownLosses}L of own games
          </span>
        </div>
        <ChevronRight
          className={`w-4 h-4 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`}
        />
      </button>
      {open && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 px-3 pb-3 pt-1 border-t border-border">
          {path.map((step) => {
            const f = fixturesById[step.fid];
            if (!f) return null;
            const winnerTeam = teamMap[step.winner];
            const isOwn = f.home === highlightTeam || f.away === highlightTeam;
            return (
              <div
                key={step.fid}
                className={`flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-sm bg-secondary/30 ${
                  isOwn ? "ring-1 ring-primary/30" : ""
                }`}
              >
                <span className="text-muted-foreground tracking-widest text-[9px] w-8">
                  {f.id}
                </span>
                <TeamLogo team={teamMap[f.home]} size={14} />
                <span className="font-display uppercase text-[10px]">{f.home}</span>
                <span className="text-muted-foreground text-[10px]">v</span>
                <span className="font-display uppercase text-[10px]">{f.away}</span>
                <TeamLogo team={teamMap[f.away]} size={14} />
                <ChevronRight className="w-3 h-3 text-muted-foreground" />
                <TeamLogo team={winnerTeam} size={14} />
                <span className="font-display uppercase text-[10px] text-primary">
                  {step.winner}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProbabilityDetail;
